import React, { useEffect, useState } from 'react';
import { exerciseLibrary } from '../../services/api';
import { Chip, Thumb, labelOf, useDebounced } from './shared';

/*
 * Substitutos de um exercício: outros exercícios que o podem trocar (equivalente, mais fácil, mais
 * difícil, sem máquina). A relação é direccional — escolher B como substituto de A não faz de A
 * substituto de B. A ordem da lista é a ordem das sugestões; as que o ginásio não consegue fazer
 * (falta a máquina) mostram-se mas ficam marcadas.
 *
 * Cada entrada: { exerciseId, name, kind, note, priority?, imageUrl, available, isActive }
 */

export default function SubstitutesEditor({ selfId, value, onChange, kinds, disabled = false }) {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 300);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = debounced.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    let alive = true;
    setSearching(true);
    exerciseLibrary.search({ q, pageSize: 8, includeInactive: true })
      .then(({ data }) => { if (alive) setResults(data.items); })
      .catch(() => { if (alive) setResults([]); })
      .finally(() => { if (alive) setSearching(false); });
    return () => { alive = false; };
  }, [debounced]);

  const taken = new Set(value.map((s) => s.exerciseId));
  const candidates = results.filter((r) => r.id !== selfId && !taken.has(r.id));

  const add = (item) => {
    onChange([...value, {
      exerciseId: item.id, name: item.name, kind: 'equivalent', note: '',
      imageUrl: item.imageUrl, available: item.available, isActive: item.isActive,
    }]);
    setQuery('');
    setResults([]);
  };

  const update = (index, patch) => onChange(value.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const remove = (index) => onChange(value.filter((_, i) => i !== index));
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[index], next[target]] = [next[target], next[index]];
    // Uma ordem escolhida de propósito passa a prioridade: 10, 20, 30…
    onChange(next.map((s, i) => ({ ...s, priority: (i + 1) * 10 })));
  };

  return (
    <div>
      <div className="lib-notice">
        Os substitutos são sugeridos por esta ordem quando alguém precisa de trocar este exercício. Os que o ginásio não consegue fazer
        (falta uma máquina) aparecem na mesma, mas marcados.
      </div>

      {value.length === 0 && <div className="lib-empty" style={{ marginBottom: 12 }}>Sem substitutos.</div>}
      {value.map((s, index) => (
        <div key={s.exerciseId} className="lib-sub">
          <div className="lib-sub-head">
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
              <Thumb src={s.imageUrl} />
              <div style={{ minWidth: 0 }}>
                <div className="lib-card-title">{s.name}</div>
                <div className="lib-chips" style={{ marginTop: 4 }}>
                  {s.available === false && <Chip tone="warn" title="Falta uma máquina neste ginásio">Sem máquina</Chip>}
                  {s.isActive === false && <Chip tone="bad">Desativado</Chip>}
                </div>
              </div>
            </div>
            {!disabled && (
              <span style={{ display: 'flex', gap: 2, flex: 'none' }}>
                <button type="button" className="lib-btn small icon ghost" aria-label="Subir" disabled={index === 0} onClick={() => move(index, -1)}>↑</button>
                <button type="button" className="lib-btn small icon ghost" aria-label="Descer" disabled={index === value.length - 1} onClick={() => move(index, 1)}>↓</button>
                <button type="button" className="lib-btn small icon ghost" aria-label={`Retirar ${s.name}`} onClick={() => remove(index)}>✕</button>
              </span>
            )}
          </div>
          <div className="lib-row">
            <div className="lib-field" style={{ marginBottom: 0 }}>
              <label htmlFor={`sub-kind-${s.exerciseId}`}>Tipo</label>
              <select id={`sub-kind-${s.exerciseId}`} className="lib-select" value={s.kind} disabled={disabled} onChange={(e) => update(index, { kind: e.target.value })}>
                {kinds.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
              </select>
            </div>
            <div className="lib-field" style={{ marginBottom: 0 }}>
              <label htmlFor={`sub-note-${s.exerciseId}`}>Nota (opcional)</label>
              <input id={`sub-note-${s.exerciseId}`} className="lib-input" value={s.note || ''} maxLength={300} disabled={disabled}
                placeholder={`Ex.: ${labelOf(kinds, s.kind)}, com halteres`} onChange={(e) => update(index, { note: e.target.value })} />
            </div>
          </div>
        </div>
      ))}

      {!disabled && (
        <div className="lib-field" style={{ marginTop: 12 }}>
          <label htmlFor="sub-search">Acrescentar substituto</label>
          <input id="sub-search" className="lib-input" type="search" value={query} placeholder="Procurar exercício pelo nome, músculo ou máquina…"
            onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
          {searching && <span className="lib-hint" role="status">A procurar…</span>}
          {query.trim().length >= 2 && !searching && candidates.length === 0 && <span className="lib-hint">Nenhum exercício encontrado.</span>}
          {candidates.length > 0 && (
            <div className="lib-picklist" role="list">
              {candidates.map((item) => (
                <div key={item.id} className="lib-pick" role="listitem">
                  <Thumb src={item.imageUrl} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{item.name}</div>
                    <div className="lib-card-sub">{item.groupLabel}{item.available ? '' : ' · sem máquina'}{item.isActive ? '' : ' · desativado'}</div>
                  </div>
                  <button type="button" className="lib-btn small" onClick={() => add(item)}>Acrescentar</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
